import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { LogOut, Package, ShoppingBag, BarChart3, Wrench, MessageSquare, FileText, Tag, Megaphone, Image as ImageIcon } from "lucide-react";
import api from "../../lib/api";

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const token = localStorage.getItem("sz_admin_token");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancel = false;
    const tick = async () => {
      try {
        const r = await api.get("/admin/messages/threads");
        if (!cancel) setUnread(r.data.total_unread || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => { cancel = true; clearInterval(id); };
  }, [token]);

  if (!token) return <Navigate to="/admin/login" replace />;

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("sz_admin_token");
    localStorage.removeItem("sz_admin_user");
    navigate("/admin/login");
  };

  const navLink = (to, label, Icon, badge = 0) => {
    const active = loc.pathname.startsWith(to);
    return (
      <Link to={to} data-testid={`admin-nav-${label.toLowerCase()}`}
        className={`relative flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition ${
          active ? "brand-bg text-white" : "text-[var(--brand-text)] hover:bg-[var(--brand-green-light)]"
        }`}
      >
        <Icon className="w-4 h-4" />
        {label}
        {badge > 0 && (
          <span className="ml-1 brand-bg text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center"
            style={active ? { background: "#fff", color: "var(--brand-green)" } : undefined}
            data-testid="admin-nav-messages-badge"
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: "#f7faf6" }}>
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/admin/orders" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
              alt="ScreenzZ" className="h-7" />
            <span className="text-xs font-bold uppercase tracking-wider muted-text">Admin</span>
          </Link>
          <nav className="flex items-center gap-2 flex-wrap justify-end">
            {navLink("/admin/dashboard", "Dashboard", BarChart3)}
            {navLink("/admin/orders", "Orders", ShoppingBag)}
            {navLink("/admin/messages", "Messages", MessageSquare, unread)}
            {navLink("/admin/products", "Products", Package)}
            {navLink("/admin/catalog", "Catalog", Wrench)}
            {navLink("/admin/templates", "Templates", FileText)}
            {navLink("/admin/discounts", "Discounts", Tag)}
            {navLink("/admin/promotions", "Promotions", Megaphone)}
            {navLink("/admin/gallery", "Photos", ImageIcon)}
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="px-4 py-2 rounded-full font-semibold text-sm border-2 border-[var(--brand-border)] hover:brand-text"
            >
              View site
            </Link>
            <button data-testid="admin-logout-btn" onClick={logout}
              className="flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm border-2 border-[var(--brand-border)]">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </nav>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-5 py-6">{children}</main>
    </div>
  );
}
